import React from 'react'
import { Badge, BadgeProps, Text, Tooltip } from '@chakra-ui/react'

/**
 * Bucket boundaries for the customer-sentiment float we get back from
 * Retell. Retell maps the four-state `user_sentiment` enum
 * (Positive/Neutral/Negative/Unknown) to `1 / 0 / -1 / 0` in
 * `server/src/phone-integrations/retell/retell.controller.ts`. We bucket
 * with a small epsilon so future provider-side jitter doesn't push the
 * neutral band into one of the colored states.
 */
const POSITIVE_THRESHOLD = 0.34
const NEGATIVE_THRESHOLD = -0.34

export type SentimentTone = 'positive' | 'neutral' | 'negative'

function getSentimentTone(value: number | null | undefined): SentimentTone | null {
    if (value == null) return null
    if (value >= POSITIVE_THRESHOLD) return 'positive'
    if (value <= NEGATIVE_THRESHOLD) return 'negative'
    return 'neutral'
}

function getSentimentColorScheme(tone: SentimentTone): string {
    switch (tone) {
        case 'positive':
            return 'green'
        case 'negative':
            return 'red'
        case 'neutral':
        default:
            return 'gray'
    }
}

function getSentimentLabel(tone: SentimentTone): string {
    switch (tone) {
        case 'positive':
            return 'Positive'
        case 'negative':
            return 'Negative'
        case 'neutral':
        default:
            return 'Neutral'
    }
}

interface SentimentBadgeProps extends Omit<BadgeProps, 'colorScheme'> {
    /** Customer sentiment value from the Call row (-1 to 1). */
    sentiment: number | null | undefined
    /**
     * Optional secondary sentiment to expose in the tooltip (typically the
     * agent's own sentiment). Useful for spotting agent/caller divergence.
     */
    agentSentiment?: number | null
}

/**
 * Compact pill that surfaces post-call customer sentiment in the calls
 * table and detail modal. Renders an em-dash for missing values rather
 * than a "Neutral" pill — there's a real difference between "we didn't
 * capture this" and "the call was emotionally flat".
 */
export default function SentimentBadge({
    sentiment,
    agentSentiment,
    ...badgeProps
}: SentimentBadgeProps) {
    const tone = getSentimentTone(sentiment)
    if (!tone) {
        return (
            <Tooltip
                label="Sentiment is computed once the call ends and the post-call analysis finishes."
                hasArrow
                placement="top"
            >
                <Text fontSize="sm" color="gray.400">
                    --
                </Text>
            </Tooltip>
        )
    }

    const agentTone = getSentimentTone(agentSentiment)
    const tooltipLabel = agentTone
        ? `Caller: ${getSentimentLabel(tone)} · Agent: ${getSentimentLabel(
              agentTone,
          )}`
        : `Customer sentiment from the post-call analysis.`

    return (
        <Tooltip label={tooltipLabel} hasArrow placement="top" openDelay={200}>
            <Badge
                colorScheme={getSentimentColorScheme(tone)}
                variant="subtle"
                rounded="md"
                px={2}
                py={0.5}
                fontSize="xs"
                fontWeight="semibold"
                {...badgeProps}
            >
                {getSentimentLabel(tone)}
            </Badge>
        </Tooltip>
    )
}
